import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TransformComponent } from 'react-zoom-pan-pinch';
import { makeStyles } from '@material-ui/styles';
import { CircularProgress, Theme } from '@material-ui/core';

import { ImageViewerData } from './types';
import { useElementSize } from 'hooks/element-size-hook';

interface Props<T> {
  data?: ImageViewerData<T>;
  isPanning: boolean;
  rotation: number;
}

interface StylesProps {
  isPanning: boolean;
}

const useStyles = makeStyles<Theme, StylesProps>(() => ({
  fullHeightWidth: {
    height: '100%',
    width: '100%',
  },
  imageWrapper: {
    height: '100%',
    width: '100%',
    cursor: (props) => (props.isPanning ? 'grabbing' : 'grab'),
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  centeredContainer: {
    height: '100%',
    display: 'grid',
    placeItems: 'center',
  },
}));

export const ImageViewerPreview = <T,>({
  data,
  isPanning,
  rotation,
}: Props<T>) => {
  const styles = useStyles({ isPanning });
  const [isLoading, setIsLoading] = useState(!!data);
  const [ref, { width, height }] = useElementSize();

  const imageSize = useMemo(() => {
    if (rotation !== 90 && rotation !== 270) {
      return { height, width };
    }

    return {
      height: width,
      width: height,
    };
  }, [height, rotation, width]);

  // shift the image back in place after rotation
  const imageShift = useMemo(() => {
    if (rotation !== 90 && rotation !== 270) {
      return 0;
    }

    const shift = (width - height) / 2;

    return rotation === 90 ? -shift : shift;
  }, [height, rotation, width]);

  useEffect(() => {
    setIsLoading(data ? true : false);
  }, [data]);

  const handleOnLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <TransformComponent
      wrapperClass={styles.fullHeightWidth}
      contentClass={styles.fullHeightWidth}
    >
      <div className={styles.imageWrapper} ref={ref}>
        {data && (
          <img
            hidden={isLoading}
            src={data.url}
            alt=""
            onLoad={handleOnLoad}
            className={styles.image}
            style={{
              transform: `rotate(${rotation}deg) translate(${imageShift}px, ${imageShift}px)`,
              ...imageSize,
            }}
          />
        )}
        {isLoading && (
          <div className={styles.centeredContainer}>
            <CircularProgress />
          </div>
        )}
        {!data && (
          <div className={styles.centeredContainer}>
            <span>Žádné data</span>
          </div>
        )}
      </div>
    </TransformComponent>
  );
};
